import { useCallback, useEffect, useRef, useState } from 'react'

import useWindowDimensions from '~/hooks/useWindowDimensions'

type UseTextFadingType = {
  marqueeTransitionSpeed?: number
}

const useTextFading = ({ marqueeTransitionSpeed = 15 }: UseTextFadingType) => {
  const containerRef = useRef<HTMLDivElement | null>(null)

  const marqueeRef = useRef<HTMLDivElement | null>(null)

  const translateRef = useRef({
    initialDistanceToTranslateRight: 0
  })

  const [transitionTime, setTransitionTime] = useState(0)

  const [translateValue, setTranslateValue] = useState(0)

  const [direction, setDirection] = useState<'left' | 'right'>('right')

  const [showRightFade, setShowRightFade] = useState(false)

  const [showLeftFade, setShowLeftFade] = useState(false)

  const windowDimensions = useWindowDimensions()

  const checkPosition = useCallback(() => {
    if (containerRef.current && marqueeRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect()

      const marqueeRect = marqueeRef.current.getBoundingClientRect()

      setShowLeftFade(marqueeRect.left < containerRect.left)

      setShowRightFade(
        Math.floor(marqueeRect.right) > Math.floor(containerRect.right)
      )
    }
  }, [])

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (containerRef.current && marqueeRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect()

        const { clientX: absoluteMousePosition } = event

        const relativeMousePosition = absoluteMousePosition - containerRect.left

        if (relativeMousePosition) {
          const containerHalf = Math.floor(containerRect.width / 2)

          if (relativeMousePosition < containerHalf) {
            setDirection('left')

            setTranslateValue(0)
          } else {
            setDirection('right')

            setTranslateValue(
              translateRef.current.initialDistanceToTranslateRight
            )
          }

          checkPosition()
        }
      }
    }

    const handleMouseLeave = () => {
      checkPosition()
    }

    const handleResize = () => {
      if (containerRef?.current && marqueeRef?.current) {
        const containerWidth = containerRef.current.offsetWidth

        const contentWidth = marqueeRef.current.scrollWidth

        const averageCharacterSizeInPixels = Math.ceil(
          marqueeRef.current.offsetWidth / marqueeRef.current.innerHTML.length
        )

        if (contentWidth > containerWidth) {
          const distanceToTranslate = containerWidth - contentWidth

          const averageDistancePerCharacter = Math.ceil(
            Math.abs(distanceToTranslate) / averageCharacterSizeInPixels
          )

          const readingTimePerCharacterInMs = marqueeTransitionSpeed / 1000

          const time = averageDistancePerCharacter / readingTimePerCharacterInMs

          setTransitionTime(time)

          setTranslateValue(distanceToTranslate)

          translateRef.current = {
            initialDistanceToTranslateRight: distanceToTranslate
          }

          setShowRightFade(true)
        } else {
          setShowRightFade(false)

          setShowLeftFade(false)
        }
      }
    }

    const currentMarqueeRef = marqueeRef.current

    currentMarqueeRef?.addEventListener('mousemove', handleMouseMove)

    currentMarqueeRef?.addEventListener('mouseleave', handleMouseLeave)

    handleResize()

    return () => {
      currentMarqueeRef?.removeEventListener('mousemove', handleMouseMove)

      currentMarqueeRef?.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [marqueeTransitionSpeed, windowDimensions, checkPosition])

  return {
    transitionTime,
    translateValue,
    direction,
    showRightFade,
    showLeftFade,
    containerRef,
    marqueeRef,
    checkPosition
  }
}

export default useTextFading
